import type {
  BridgeRow,
  EarnedValue,
  MarginBridge,
  ProjectAnalytics,
  SectionDeviation,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, percent, tenge, tengeShort } from '../../lib/format';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone } from '../../ui/feedback';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';

type Tab = 'ev' | 'bridge' | 'sections';
const TABS: Array<[Tab, string]> = [
  ['bridge', 'Мост маржи'],
  ['sections', 'Отклонения по разделам'],
  ['ev', 'Освоенный объём'],
];

const idx = (v: number | null) => (v === null ? '—' : String(v).replace('.', ','));
const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${tengeShort(Math.abs(v))}`;
const tone = (v: number): string => (v > 0.005 ? 'text-success' : v < -0.005 ? 'text-danger' : 'text-ink');
const SEVERITY: Record<string, Tone> = { critical: 'danger', warning: 'warning', info: 'neutral' };

export const projectAnalyticsQuery = (id: string) => ({
  queryKey: ['analytics', 'project', id],
  queryFn: () => api<ProjectAnalytics>(`/analytics/projects/${id}`),
});

export function ProjectPanel({
  projectId,
  tab,
  onTab,
}: {
  projectId: string;
  tab: string | undefined;
  onTab: (t: Tab) => void;
}) {
  const q = useQuery(projectAnalyticsQuery(projectId));
  const active: Tab = TABS.some(([k]) => k === tab) ? (tab as Tab) : 'bridge';
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const a = q.data;
  return (
    <section aria-label={`Разбор проекта ${a.code}`} className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-[20px] leading-7 font-semibold text-ink">
            {a.code} · {a.name}
          </h2>
          <p className="text-[13px] text-ink-2">Состояние на {date(a.asOf)}</p>
        </div>
        <div role="tablist" aria-label="Разбор проекта" className="flex flex-wrap gap-2">
          {TABS.map(([k, title]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={active === k}
              onClick={() => onTab(k)}
              className={clsx(
                'min-h-11 rounded-md border px-4 text-[14px] font-medium md:min-h-9',
                active === k
                  ? 'border-ink bg-ink text-on-ink'
                  : 'border-line-strong bg-surface text-ink hover:underline',
              )}
            >
              {title}
            </button>
          ))}
        </div>
      </div>
      {a.costFacts ? <CostFactsView facts={a.costFacts} projectId={a.projectId} /> : null}
      {active === 'bridge' ? <BridgeView a={a} /> : null}
      {active === 'sections' ? <SectionsView a={a} /> : null}
      {active === 'ev' ? <EvView a={a} /> : null}
      <Risks a={a} />
    </section>
  );
}

/* ─────────── АН-02: мост маржи ─────────── */

function DocLink({ row }: { row: BridgeRow }) {
  if (!row.link) return null;
  return (
    <Link
      to={row.link.path}
      className="inline-flex min-h-11 items-center gap-1 text-[13px] text-accent-text underline-offset-4 hover:underline md:min-h-8"
    >
      Открыть
      <ExternalLink className="size-3.5" aria-hidden />
    </Link>
  );
}

function RowList({ rows }: { rows: BridgeRow[] }) {
  if (!rows.length) return <p className="px-4 py-3 text-[13px] text-ink-2">Строк нет.</p>;
  let group: string | null | undefined;
  return (
    <ul className="divide-y divide-line">
      {rows.map((r) => {
        const head = r.group && r.group !== group ? r.group : null;
        group = r.group;
        return (
          <li key={`${r.title}|${r.link?.id ?? ''}|${r.amount}`}>
            {head ? (
              <p className="bg-surface-2 px-4 py-1.5 text-[12px] font-medium text-ink-2">Причина: {head}</p>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-[14px] [overflow-wrap:anywhere] text-ink">{r.title}</p>
                {r.detail ? <p className="text-[12px] text-ink-2">{r.detail}</p> : null}
              </div>
              <span className={clsx('num text-[14px] font-medium', tone(r.amount))}>{signed(r.amount)}</span>
              <DocLink row={r} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Bar({ value, max }: { value: number; max: number }) {
  const w = max > 0 ? Math.max(2, (Math.abs(value) / max) * 100) : 0;
  return (
    <div className="h-2 rounded-full bg-tile" aria-hidden>
      <div
        className={clsx('h-2 rounded-full', value >= 0 ? 'bg-success' : 'bg-danger')}
        style={{ width: `${Math.min(100, w)}%` }}
      />
    </div>
  );
}

function Total({ label, p }: { label: string; p: MarginBridge['planned'] }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-3">
      <span className="text-[15px] font-semibold text-ink">{label}</span>
      <span className="num text-[18px] font-semibold text-ink">
        {tengeShort(p.margin)}{' '}
        <span className="text-[13px] font-normal text-ink-2">({percent(p.marginPct)})</span>
      </span>
    </div>
  );
}

function CostFactsView({
  facts,
  projectId,
}: {
  facts: NonNullable<ProjectAnalytics['costFacts']>;
  projectId: string;
}) {
  const rows: Array<{ label: string; value: string; tone?: string; title: string }> = [
    {
      label: `Списано со склада (кроме отчётов)${facts.consumedUnpriced ? ` (${facts.consumedUnpriced} без цены)` : ''}`,
      value: tengeShort(facts.consumed),
      title: 'Проведённые списания и расход у получателя на проект по учётной цене.',
    },
    {
      label: 'Начислено с площадки (с материалами отчёта)',
      value: tengeShort(facts.accrued),
      title:
        'Оценки людей и техники из принятых отчётов плюс материалы из списаний отчёта смены. Подряд — когда появятся акты КС-2.',
    },
    {
      label: `Перерасход против нормы${facts.overNormLines ? ` (${facts.overNormLines} матер.${facts.overNormUnpriced ? `, ${facts.overNormUnpriced} без цены` : ''})` : ''}`,
      value: tengeShort(facts.overNorm),
      tone: facts.overNorm > 0 ? 'text-danger' : undefined,
      title:
        'СК-22: отклонение выше порога компании × средняя цена прихода. Подробности — склад → «Расход по факту против нормы».',
    },
  ];
  return (
    <Panel
      title="Факт затрат"
      meta="склад, площадка и перерасход против нормы"
      actions={
        <Link
          to="/warehouse"
          search={{ tab: 'reports' }}
          className="inline-flex min-h-11 items-center gap-1 text-[13px] text-accent-text underline-offset-2 hover:underline"
        >
          Отчёт расхода
          <ExternalLink className="size-3.5" aria-hidden />
        </Link>
      }
      bodyClassName="p-0"
    >
      <div className="grid grid-cols-1 gap-px bg-line sm:grid-cols-3">
        {rows.map((r) => (
          <div key={r.label} className="bg-surface px-4 py-3" title={r.title}>
            <span className="block text-[12px] text-ink-2">{r.label}</span>
            <span className={clsx('num block text-[15px] font-medium', r.tone ?? 'text-ink')}>{r.value}</span>
          </div>
        ))}
      </div>
      <p className="border-t border-line px-4 py-2 text-[12px] text-ink-2">
        <Link to="/projects/$projectId" params={{ projectId }} className="underline">
          Карточка проекта
        </Link>
        {' · '}бюджет и маржа
      </p>
    </Panel>
  );
}

function BridgeView({ a }: { a: ProjectAnalytics }) {
  const b = a.bridge;
  if (!b)
    return (
      <EmptyState title="Мост маржи не показан">
        {a.seesMargin
          ? 'У проекта нет бюджета: мост строится от сметы и бюджета.'
          : 'Маржа и мост считаются в деньгах: их открывает уровень доступа к деньгам «Все деньги» и право видеть бюджет проекта.'}
      </EmptyState>
    );
  const max = Math.max(1, ...b.steps.map((s) => Math.abs(s.amount)));
  return (
    <Panel
      title="Мост маржи: от плана к прогнозу"
      meta={
        b.base === 'contract'
          ? 'от подписанного договора с заказчиком'
          : 'от сметы: доходного договора ещё нет'
      }
      bodyClassName="p-0"
    >
      <Total label="Плановая маржа" p={b.planned} />
      <ul className="divide-y divide-line border-t border-line">
        {b.steps.map((s) => (
          <li key={s.key}>
            <details className="group">
              <summary className="flex min-h-11 cursor-pointer list-none flex-col gap-1.5 px-4 py-3 hover:bg-tile/40">
                <span className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-[14px] text-ink">
                    <ChevronDown
                      className="size-4 shrink-0 transition-transform group-open:rotate-180"
                      aria-hidden
                    />
                    {s.title}
                    <span className="text-[12px] text-ink-2">({s.rows.length})</span>
                  </span>
                  <span className={clsx('num text-[15px] font-semibold', tone(s.amount))}>
                    {signed(s.amount)}
                  </span>
                </span>
                <Bar value={s.amount} max={max} />
              </summary>
              {s.note ? (
                <p className="border-t border-line px-4 py-2 text-[12px] text-ink-2">{s.note}</p>
              ) : null}
              <div className="border-t border-line">
                <RowList rows={s.rows} />
              </div>
            </details>
          </li>
        ))}
      </ul>
      <div className="border-t border-line">
        <Total label="Прогнозная маржа" p={b.forecast} />
        {Math.abs(b.unexplained) > 0.01 ? (
          <p className="px-4 pb-3 text-[12px] text-ink-2">
            Не разнесено по строкам моста: {tenge(b.unexplained)} (округления и данные, которые мост не
            видит).
          </p>
        ) : null}
      </div>
      {b.outside.length ? (
        <div className="border-t border-line">
          <h3 className="px-4 pt-3 text-[14px] font-semibold text-ink">Вне прогноза, справочно</h3>
          <p className="px-4 text-[12px] text-ink-2">
            Эти затраты в прогноз маржи не входят, но съедают её: техника и топливо по путевым листам.
          </p>
          <ul className="divide-y divide-line">
            {b.outside.map((o) => (
              <li key={o.key}>
                <details className="group">
                  <summary className="flex min-h-11 cursor-pointer list-none flex-wrap items-center justify-between gap-x-3 px-4 py-2 hover:bg-tile/40">
                    <span className="flex items-center gap-2 text-[14px] text-ink">
                      <ChevronDown
                        className="size-4 shrink-0 transition-transform group-open:rotate-180"
                        aria-hidden
                      />
                      {o.title}
                    </span>
                    <span className="num text-[14px] font-medium text-ink">
                      {o.amount === null
                        ? (o.volume ?? '—')
                        : `${signed(o.amount)}${o.volume ? ` · ${o.volume}` : ''}`}
                    </span>
                  </summary>
                  {o.note ? (
                    <p className="border-t border-line px-4 py-2 text-[12px] text-ink-2">{o.note}</p>
                  ) : null}
                  {o.rows.length ? (
                    <div className="border-t border-line">
                      <RowList rows={o.rows} />
                    </div>
                  ) : null}
                </details>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Panel>
  );
}

/* ─────────── АН-03: отклонения по разделам ─────────── */

function SectionCard({ s, projectId }: { s: SectionDeviation; projectId: string }) {
  const over = s.costDeviation > 0.005;
  return (
    <li className="flex flex-col gap-3 px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <p className="text-[15px] font-medium [overflow-wrap:anywhere] text-ink">
            {s.code ? `${s.code}. ` : ''}
            {s.name}
          </p>
          {s.sectionCode ? <p className="text-[12px] text-ink-2">шифр {s.sectionCode}</p> : null}
        </div>
        <Link
          to={`/projects/${projectId}/budget`}
          className="inline-flex min-h-11 items-center text-[13px] text-accent-text underline-offset-4 hover:underline md:min-h-8"
        >
          Раздел бюджета
        </Link>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] md:grid-cols-4">
        <div>
          <dt className="text-ink-2">План себестоимости</dt>
          <dd className="num text-ink">{tengeShort(s.planCost)}</dd>
        </div>
        <div>
          <dt className="text-ink-2">Прогноз</dt>
          <dd className="num text-ink">{tengeShort(s.forecastCost)}</dd>
        </div>
        <div>
          <dt className="text-ink-2">Отклонение</dt>
          <dd
            className={clsx(
              'num font-medium',
              over ? 'text-danger' : s.costDeviation < -0.005 ? 'text-success' : 'text-ink',
            )}
          >
            {signed(s.costDeviation)}
            {s.costDeviationPct !== null ? ` (${percent(s.costDeviationPct)})` : ''}
          </dd>
        </div>
        <div>
          <dt className="text-ink-2">Срок</dt>
          <dd className="num text-ink">
            {s.time ? (
              <>
                {s.time.slipDays === null
                  ? 'нет расчёта'
                  : s.time.slipDays > 0
                    ? `+${s.time.slipDays} дн`
                    : 'по плану'}
                {s.time.spi !== null ? (
                  <span className="block text-[12px] text-ink-2">SPI {idx(s.time.spi)}</span>
                ) : null}
                {s.time.overdueActivities ? (
                  <span className="block text-[12px] text-danger">
                    просрочено работ: {s.time.overdueActivities}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-ink-3">нет работ с этим шифром</span>
            )}
          </dd>
        </div>
      </dl>
      {s.reasons.length ? (
        <p className="text-[13px] text-ink">
          <span className="text-ink-2">Причина: </span>
          {s.reasons.join('; ')}
        </p>
      ) : null}
      {s.changes.length ? (
        <ul className="flex flex-col">
          {s.changes.map((c) => (
            <li key={c.id}>
              <Link
                to={`/changes/${c.id}`}
                className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px] text-accent-text underline-offset-4 hover:underline md:min-h-8"
              >
                {c.number} {c.title}
                <span className="text-ink-2">
                  {c.costDelta !== null ? signed(c.costDelta) : ''}
                  {c.daysDelta ? ` · ${c.daysDelta > 0 ? '+' : ''}${c.daysDelta} дн` : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      {s.contracts.length ? (
        <ul className="flex flex-wrap gap-x-4">
          {s.contracts.map((c) => (
            <li key={c.id}>
              <Link
                to={`/contracts/${c.id}`}
                className="inline-flex min-h-11 items-center text-[13px] text-accent-text underline-offset-4 hover:underline md:min-h-8"
              >
                Договор {c.number ?? ''}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      {s.risks.length ? (
        <ul className="flex flex-col gap-1">
          {s.risks.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 text-[13px]">
              <Badge tone={SEVERITY[r.severity] ?? 'neutral'}>риск</Badge>
              {r.title}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function SectionsView({ a }: { a: ProjectAnalytics }) {
  if (!a.sections)
    return (
      <EmptyState title="Отклонения по разделам не показаны">
        {a.seesMargin
          ? 'У проекта нет бюджета с разделами.'
          : 'Себестоимость разделов — в деньгах: нужен уровень доступа к деньгам «Все деньги» и право видеть бюджет проекта.'}
      </EmptyState>
    );
  const sorted = [...a.sections].sort((x, y) => y.costDeviation - x.costDeviation);
  return (
    <Panel
      title="Отклонения по разделам"
      meta="себестоимость по договорам и изменениям против плана раздела"
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-line">
        {sorted.map((s) => (
          <SectionCard key={s.lineId} s={s} projectId={a.projectId} />
        ))}
      </ul>
      <p className="border-t border-line px-4 py-3 text-[12px] text-ink-2">
        Изменения, которые не привязаны к разделу бюджета, видны в мосте маржи, а в этой таблице не отражены.
        Срок раздела берётся из графика по шифру раздела проекта.
      </p>
    </Panel>
  );
}

/* ─────────── АН-01: освоенный объём ─────────── */

function Curve({ e, eac }: { e: EarnedValue; eac: number | null }) {
  if (e.curve.length < 2) return null;
  const max = Math.max(1, e.total.bac, eac ?? 0, ...e.curve.map((c) => Math.max(c.pv, c.ev ?? 0, c.ac ?? 0)));
  const lastAc = [...e.curve].reverse().find((c) => c.ac != null);
  const lastAcIdx = lastAc ? e.curve.indexOf(lastAc) : -1;
  const yOf = (v: number) => 100 - (v / max) * 96;
  const W = e.curve.length - 1;
  const path = (get: (c: EarnedValue['curve'][number]) => number | null) =>
    e.curve
      .map((c, i) => [i, get(c)] as const)
      .filter(([, v]) => v != null)
      .map(([i, v], j) => `${j ? 'L' : 'M'}${(i / W) * 1000},${100 - ((v as number) / max) * 96}`)
      .join(' ');
  return (
    <Panel title="Кривые нарастающим итогом" meta="по неделям">
      <svg
        viewBox="0 0 1000 100"
        preserveAspectRatio="none"
        className="h-44 w-full"
        role="img"
        aria-label="Кривые плана, освоенного и фактических затрат"
      >
        <path
          d={path((c) => c.pv)}
          fill="none"
          stroke="var(--color-ink-2)"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={path((c) => c.ev)}
          fill="none"
          stroke="var(--color-success)"
          strokeWidth={2.5}
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={path((c) => c.ac)}
          fill="none"
          stroke="var(--color-danger)"
          strokeWidth={2.5}
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1={0}
          x2={1000}
          y1={yOf(e.total.bac)}
          y2={yOf(e.total.bac)}
          stroke="var(--color-ink-2)"
          strokeWidth={1}
          strokeDasharray="2 5"
          vectorEffect="non-scaling-stroke"
        />
        {eac != null && lastAc?.ac != null ? (
          <line
            x1={(lastAcIdx / W) * 1000}
            y1={yOf(lastAc.ac)}
            x2={1000}
            y2={yOf(eac)}
            stroke="var(--color-danger)"
            strokeWidth={2.5}
            strokeDasharray="8 6"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>
      <div className="mt-1 flex justify-between text-[12px] text-ink-2">
        <span>вверху шкалы {tengeShort(max)}</span>
        <span>пунктир по горизонтали — бюджет работ</span>
      </div>
      <div className="mt-2 flex flex-wrap justify-between gap-2 text-[12px] text-ink-2">
        <span>{date(e.curve[0]?.week)}</span>
        <span className="inline-flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-0.5 w-4 bg-ink-2" /> план (PV)
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-0.5 w-4 bg-success" /> освоено (EV)
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-0.5 w-4 bg-danger" /> факт (AC)
          </span>
          {eac != null ? (
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-4 border-t-2 border-dashed border-danger" /> прогноз затрат
            </span>
          ) : null}
        </span>
        <span>{date(e.curve.at(-1)?.week)}</span>
      </div>
    </Panel>
  );
}

type ForecastMethod = 'byCpi' | 'byCpiSpi' | 'byPlan';
const FORECAST_METHODS: Array<[ForecastMethod, string, string]> = [
  ['byCpi', 'По CPI', 'остаток работ идёт с той же эффективностью затрат, что и до сих пор'],
  ['byCpiSpi', 'По CPI и SPI', 'учитывает и затраты, и отставание от графика'],
  ['byPlan', 'По остатку плана', 'остаток работ стоит столько, сколько заложено в плане'],
];
const forecastOf = (e: EarnedValue, m: ForecastMethod) => e.forecasts[m];

/** Три способа прогноза затрат: выбор меняет цифры и пунктир на кривой (АН-01) */
function ForecastPicker({
  e,
  method,
  onMethod,
}: {
  e: EarnedValue;
  method: ForecastMethod;
  onMethod: (m: ForecastMethod) => void;
}) {
  const eac = forecastOf(e, method);
  const over = eac === null ? null : eac - e.total.bac;
  return (
    <div className="mt-4 flex flex-col gap-3">
      <fieldset className="m-0 grid min-w-0 gap-2 border-0 p-0 md:grid-cols-3">
        <legend className="sr-only">Способ прогноза затрат</legend>
        {FORECAST_METHODS.map(([key, title, hint]) => (
          <button
            key={key}
            type="button"
            aria-pressed={method === key}
            onClick={() => onMethod(key)}
            className={clsx(
              'flex min-h-11 flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left',
              method === key ? 'border-ink bg-tile' : 'border-line hover:bg-tile/50',
            )}
          >
            <span className="flex w-full items-center justify-between gap-2 text-[14px] font-semibold">
              {title}
              <span className="num font-normal">{tengeShort(e.forecasts[key])}</span>
            </span>
            <span className="text-[12px] text-ink-2">{hint}</span>
          </button>
        ))}
      </fieldset>
      <p className="text-[14px]">
        Прогноз затрат на завершение ({FORECAST_METHODS.find((m) => m[0] === method)?.[1].toLowerCase()}):{' '}
        <b>{tengeShort(eac)}</b>
        {over === null ? (
          '.'
        ) : (
          <>
            ; к бюджету работ {over > 0 ? 'перерасход ' : 'экономия '}
            <b className={clsx(over > 0 && 'text-danger')}>{tengeShort(Math.abs(over))}</b>.
          </>
        )}
      </p>
    </div>
  );
}

function EvView({ a }: { a: ProjectAnalytics }) {
  const [method, setMethod] = useState<ForecastMethod>('byCpi');
  const e = a.ev;
  if (!e)
    return <EmptyState title="Освоенный объём не показан">{a.evReason ?? 'Нет данных графика.'}</EmptyState>;
  const t = e.total;
  const tiles: Array<[string, string, boolean]> = [
    ['Бюджет работ (BAC)', tengeShort(t.bac), false],
    ['План на дату (PV)', tengeShort(t.pv), false],
    ['Освоено (EV)', tengeShort(t.ev), false],
    ['Факт затрат (AC)', tengeShort(t.ac), false],
    ['SPI — по срокам', idx(t.spi), (t.spi ?? 1) < 1],
    ['CPI — по затратам', idx(t.cpi), (t.cpi ?? 1) < 1],
  ];
  return (
    <div className="flex flex-col gap-6">
      <Panel title="Освоенный объём" meta={`${e.baseline?.name ?? ''} · на ${date(e.date)}`}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {tiles.map(([title, v, bad]) => (
            <div key={title} className="rounded-md border border-line bg-surface px-3 py-2">
              <div className="text-[12px] text-ink-2">{title}</div>
              <div className={clsx('num text-[17px] font-semibold', bad && 'text-danger')}>{v}</div>
            </div>
          ))}
        </div>
        <ForecastPicker e={e} method={method} onMethod={setMethod} />
        <p className="text-[14px]">
          Окончание по базовому плану {date(e.finishBaseline)}, прогноз по SPI{' '}
          <b
            className={clsx(
              e.finishForecast && e.finishBaseline && e.finishForecast > e.finishBaseline && 'text-danger',
            )}
          >
            {date(e.finishForecast)}
          </b>
          .
        </p>
        <p className="mt-1 text-[12px] text-ink-2">
          Факт затрат: {e.acSources.join('; ')}.
          {e.missingRates.length ? ` Нет ставки, факт не учтён: ${e.missingRates.join(', ')}.` : ''}
          {e.withoutCost
            ? ` Работ без стоимости в базовом плане: ${e.withoutCost}, в расчёт они не входят.`
            : ''}
        </p>
      </Panel>
      <Curve e={e} eac={forecastOf(e, method)} />
      <Panel title="По разделам графика" bodyClassName="p-0">
        <ScrollX>
          <table className="w-full min-w-[640px] text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2 pr-2 pl-3">Раздел</th>
                <th className="py-2 pr-2 text-right">BAC</th>
                <th className="py-2 pr-2 text-right">EV</th>
                <th className="py-2 pr-2 text-right">AC</th>
                <th className="py-2 pr-2 text-right">SPI</th>
                <th className="py-2 pr-2 text-right">CPI</th>
                <th className="py-2 pr-3 text-right">EAC</th>
              </tr>
            </thead>
            <tbody>
              {e.groups.map((g) => (
                <tr key={g.key || g.name} className="border-t border-line">
                  <td className="py-1.5 pr-2 pl-3">{g.name}</td>
                  <td className="num py-1.5 pr-2 text-right">{tengeShort(g.bac)}</td>
                  <td className="num py-1.5 pr-2 text-right">{tengeShort(g.ev)}</td>
                  <td className="num py-1.5 pr-2 text-right">{tengeShort(g.ac)}</td>
                  <td className={clsx('num py-1.5 pr-2 text-right', (g.spi ?? 1) < 1 && 'text-danger')}>
                    {idx(g.spi)}
                  </td>
                  <td className={clsx('num py-1.5 pr-2 text-right', (g.cpi ?? 1) < 1 && 'text-danger')}>
                    {idx(g.cpi)}
                  </td>
                  <td className="num py-1.5 pr-3 text-right">{tengeShort(g.eac)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </Panel>
    </div>
  );
}

/* ─────────── Риски проекта ─────────── */

function Risks({ a }: { a: ProjectAnalytics }) {
  if (!a.risks.length) return null;
  return (
    <Panel
      title="Что требует внимания в проекте"
      meta="открытые находки разделов портала"
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-line">
        {a.risks.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2">
            <Badge tone={SEVERITY[r.severity] ?? 'neutral'}>
              {r.severity === 'critical' ? 'блокер' : r.severity === 'warning' ? 'вопрос' : 'к сведению'}
            </Badge>
            <span className="min-w-0 flex-1 text-[14px] [overflow-wrap:anywhere] text-ink">{r.title}</span>
            {r.path ? (
              <Link
                to={r.path}
                className="inline-flex min-h-11 items-center text-[13px] text-accent-text underline-offset-4 hover:underline md:min-h-8"
              >
                Открыть
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
